import { AnalyticsConfig } from '@faro/study-designer-model';
import { UserInfo } from '@faro/user-management-model';
import React, { ReactElement, useEffect } from 'react';
import { createSafeContext } from '../../../../util/createSafeContext';
import { useAnalyticsInstance } from './AnalyticsInstance';
import { BaseEvent } from './BaseEvent';

interface AnalyticsContextValue {
    createTracker: <EventMap extends Record<keyof EventMap, BaseEvent>>() => <T extends keyof EventMap>(
        eventName: T,
        payload: EventMap[T]
    ) => void;
}

const [AnalyticsProviderInner, useAnalyticsContext] = createSafeContext<AnalyticsContextValue>('AnalyticsContext');

interface AnalyticsProviderProps {
    config?: AnalyticsConfig;
    userInfo?: UserInfo;
    tenant: string;
    children: React.ReactNode;
}

const AnalyticsProvider = (props: AnalyticsProviderProps): ReactElement => {
    const { config, userInfo, tenant, children } = props;
    const enabled = config?.enabled ?? false;
    const analyticsInstance = useAnalyticsInstance(config);

    useEffect(() => {
        if (enabled && userInfo?.id && analyticsInstance) {
            const emailMatch = /^.+@([^@]+)$/.exec(userInfo?.email ?? '');
            const emailDomain = emailMatch ? emailMatch[1] : 'unknown';
            const fsdBasePath = document.querySelector('base')?.getAttribute('href') ?? '/';
            analyticsInstance.identify(userInfo?.id, { emailDomain, tenant, fsdBasePath });
        }
    }, [enabled, userInfo?.id, userInfo?.email, tenant, analyticsInstance]);

    const createTracker = <EventMap extends Record<keyof EventMap, BaseEvent>>() => {
        return <T extends keyof EventMap>(eventName: T, payload: EventMap[T]) => {
            if (enabled && analyticsInstance) {
                analyticsInstance.track(String(eventName), payload);
            }
        };
    };

    const contextValue = { createTracker };

    return <AnalyticsProviderInner value={contextValue}>{children}</AnalyticsProviderInner>;
};

export { AnalyticsProvider, AnalyticsProviderInner, useAnalyticsContext };
